import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import { errorHandler, successHandler } from "../../../helpers/handlers";
import api from "../../../config/axios";
import { logout } from "../auth";

const initialState = {
  referrals: [],
  pendingPayouts: [],
  ambassadorRewards: [],
  settings: { rewardPercentage: 10, lastUpdatedAt: null, lastUpdatedBy: null },
  pageLoading: false,
  pendingLoading: false,
  actionLoading: false,
  settingsLoading: false,
  rewardsLoading: false,
  error: null,
  nextPageToken: null,
};

const slice = createSlice({
  name: "referrals",
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    builder
      .addCase(logout.fulfilled, () => initialState)

      .addCase(fetchReferrals.pending, (state) => {
        state.pageLoading = true;
        state.error = null;
      })
      .addCase(fetchReferrals.fulfilled, (state, action) => {
        state.pageLoading = false;
        state.referrals = action.payload.referrals;
        state.nextPageToken = action.payload.nextPageToken;
      })
      .addCase(fetchReferrals.rejected, (state, action) => {
        state.pageLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchPendingPayouts.pending, (state) => {
        state.pendingLoading = true;
      })
      .addCase(fetchPendingPayouts.fulfilled, (state, action) => {
        state.pendingLoading = false;
        state.pendingPayouts = action.payload.pendingPayouts;
      })
      .addCase(fetchPendingPayouts.rejected, (state, action) => {
        state.pendingLoading = false;
        state.error = action.payload;
      })

      .addCase(triggerPayout.pending, (state) => {
        state.actionLoading = true;
      })
      .addCase(triggerPayout.fulfilled, (state) => {
        state.actionLoading = false;
      })
      .addCase(triggerPayout.rejected, (state, action) => {
        state.actionLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchReferralSettings.pending, (state) => {
        state.settingsLoading = true;
      })
      .addCase(fetchReferralSettings.fulfilled, (state, action) => {
        state.settingsLoading = false;
        state.settings = action.payload;
      })
      .addCase(fetchReferralSettings.rejected, (state, action) => {
        state.settingsLoading = false;
        state.error = action.payload;
      })

      .addCase(updateReferralSettings.pending, (state) => {
        state.settingsLoading = true;
      })
      .addCase(updateReferralSettings.fulfilled, (state, action) => {
        state.settingsLoading = false;
        state.settings.rewardPercentage = action.payload.rewardPercentage;
      })
      .addCase(updateReferralSettings.rejected, (state, action) => {
        state.settingsLoading = false;
        state.error = action.payload;
      })

      .addCase(fetchAmbassadorRewards.pending, (state) => {
        state.rewardsLoading = true;
        state.ambassadorRewards = [];
      })
      .addCase(fetchAmbassadorRewards.fulfilled, (state, action) => {
        state.rewardsLoading = false;
        state.ambassadorRewards = action.payload;
      })
      .addCase(fetchAmbassadorRewards.rejected, (state, action) => {
        state.rewardsLoading = false;
        state.error = action.payload;
      });
  },
});

export const fetchReferrals = createAsyncThunk(
  "referrals/fetch",
  async (params = {}, { rejectWithValue }) => {
    try {
      const response = await api.get("/admin/referrals", { params });
      return response.data.payload;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchPendingPayouts = createAsyncThunk(
  "referrals/pendingPayouts",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/admin/referrals/pending-payouts");
      return response.data.payload;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export const triggerPayout = createAsyncThunk(
  "referrals/triggerPayout",
  async ({ ambassadorId, note }, { rejectWithValue, dispatch }) => {
    try {
      const response = await api.post(`/admin/referrals/${ambassadorId}/payout`, { note });
      successHandler("Payout recorded successfully");
      dispatch(fetchPendingPayouts());
      return response.data.payload;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchReferralSettings = createAsyncThunk(
  "referrals/fetchSettings",
  async (_, { rejectWithValue }) => {
    try {
      const response = await api.get("/admin/referrals/settings");
      return response.data.payload;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export const fetchAmbassadorRewards = createAsyncThunk(
  "referrals/ambassadorRewards",
  async (ambassadorId, { rejectWithValue }) => {
    try {
      const response = await api.get("/admin/referrals", { params: { ambassadorId } });
      return response.data.payload.referrals;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export const updateReferralSettings = createAsyncThunk(
  "referrals/updateSettings",
  async ({ rewardPercentage }, { rejectWithValue }) => {
    try {
      const response = await api.put("/admin/referrals/settings", { rewardPercentage });
      successHandler("Reward percentage updated");
      return response.data.payload;
    } catch (error) {
      errorHandler(error);
      return rejectWithValue(error.message);
    }
  }
);

export default slice.reducer;
